import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";

Chart.register(ArcElement, Tooltip, Legend);

const DoughnutChart = ({ title, chartData }) => {
  // Data preparation based on chart type
  let labels = [];
  let dataValues = [];

  if (title === "Providers") {
    labels = ["Compliant", "Non-Compliant", "Invited", "Self-Registered", "Registered", "Deactivated"];
    dataValues = [
      Number(chartData?.compliant || 0),
      Number(chartData?.non_compliant || chartData?.nonCompliant || 0),
      Number(chartData?.invited || 0),
      Number(chartData?.self_registered || 0),
      Number(chartData?.registered || chartData?.assigned || 0),
      Number(chartData?.deactivated || chartData?.closed || 0),
    ];
  } else if (title === "Workers") {
    labels = ["Compliant", "Non-Compliant", "Fractional", "Site Access App", "Invited"];
    dataValues = [
      Number(chartData?.compliant || 0),
      Number(chartData?.non_compliant || 0),
      Number(chartData?.fractional || 0),
      Number(chartData?.site_access_app || 0),
      Number(chartData?.invited || 0),
    ];
  } else if (title === "Clients") {
    labels = ["Active", "Deactivated"];
    dataValues = [
      Number(chartData?.active_clients || 0),
      Number(chartData?.deactivated_clients || 0),
    ];
  }

  let bgColors = [];
  if (title === "Providers") {
    bgColors = [
      "#155EEF", // Compliant (Dark Blue)
      "#2E90FA", // Non-Compliant (Blue)
      "#84CAFF", // Invited (Light Blue)
      "#D1E9FF", // Self-Registered (Pale Blue)
      "#12B76A", // Registered (Green)
      "#7CD4FD", // Deactivated (Cyan)
    ];
  } else if (title === "Workers") {
    bgColors = [
      "#69C195", // Compliant
      "#F97066", // Non-Compliant
      "#98A2B3", // Fractional
      "#84BFF3", // Site Access App
      "#FDB022", // Invited
    ];
  } else if (title === "Clients") {
    bgColors = [
      "#86E5B2", // Active
      "#D0D5DD", // Deactivated
    ];
  }

  const data = {
    labels: labels,
    datasets: [
      {
        label: "Status",
        data: dataValues,
        backgroundColor: bgColors,
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false, // Hide default legend
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => Number(a) + Number(b), 0);
            const percentage = ((context.raw / total) * 100).toFixed(2);
            return `${context.label}: ${context.raw} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "70%", // Makes it a doughnut chart
  };

  // Calculate total for center text using the unique counts from backend
  let totalItems = 0;
  if (title === "Clients") {
    totalItems = Number(chartData?.active_clients || 0) + Number(chartData?.deactivated_clients || 0);
  } else {
    // Sum segments to ensure consistency between chart and center total
    totalItems = dataValues.reduce((a, b) => a + Number(b), 0);
  }

  return (
    <div className="flex items-center gap-5 flex-nowrap">
      <div className="relative w-48 h-48 flex-shrink-0">
        <Doughnut data={data} options={options} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
          <h3 className="text-xl font-bold">{totalItems}</h3>
          <p className="text-[10px] leading-tight text-gray-500">Total {title}</p>
        </div>
      </div>

      <div className="flex-grow">
        <ul className="list-none p-0 m-0">
          {labels.map((label, index) => (
            <li key={index} className="flex items-center mb-1.5 whitespace-nowrap">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full mr-2 flex-shrink-0"
                style={{ backgroundColor: data.datasets[0].backgroundColor[index] }}
              ></span>
              <span className="text-gray-700 text-xs font-medium">
                {label} {dataValues[index]}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default DoughnutChart;